import React, { useState, useEffect } from 'react';
import {
  Plus, Edit, Trash2, Search, Camera, Monitor,
  Calendar, Package, MapPin, Save, X, Copy, Filter, Download, QrCode,
  ChevronDown, Eye, EyeOff, Image as ImageIcon, AlertTriangle
} from 'lucide-react';
import Modal from './Modal';
import * as XLSX from 'xlsx';
import CameraScreenshotCapture from './CameraScreenshotCapture';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';

interface Camera {
  id: number;
  brand: string;
  alias?: string;
  model: string;
  nvr_id: number;
  nvr_name: string;
  location_name: string;
  channel_number: number;
  ip_address: string;
  warranty_start_date: string;
  warranty_end_date: string;
  inventory_number: string;
  installation_location: string;
  notes: string;
  screenshot_path?: string;
  created_at: string;
}

interface NVR {
  id: number;
  name: string;
  ip_address: string;
  location_name: string;
}

interface CameraBrand {
  id: number;
  name: string;
  models: { id: number; name: string }[];
}

const maskIpAddress = (ip: string): string => {
  if (!ip) return '';
  const parts = ip.split('.');
  if (parts.length === 4) {
    return `X.X.${parts[2]}.${parts[3]}`;
  }
  return ip;
};

const CamerasPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [nvrs, setNvrs] = useState<NVR[]>([]);
  const [cameraBrands, setCameraBrands] = useState<CameraBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('cameras', setSearchTerm);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedNvr, setSelectedNvr] = useState<NVR | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Filters
  const [cameraNameFilter, setCameraNameFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingCamera, setEditingCamera] = useState<Camera | null>(null);
  const [showBrandModal, setShowBrandModal] = useState(false);

  // Arıza kaydı modal state
  const [showFaultModal, setShowFaultModal] = useState(false);
  const [faultCamera, setFaultCamera] = useState<Camera | null>(null);
  const [faultFormData, setFaultFormData] = useState({
    fault_description: 'Görüntü Gelmiyor',
    notes: ''
  });
  const [faultImage, setFaultImage] = useState<File | null>(null);
  const [faultImagePreview, setFaultImagePreview] = useState<string | null>(null);
  const [faultSubmitting, setFaultSubmitting] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [previewScreenshot, setPreviewScreenshot] = useState<{camera: Camera, path: string} | null>(null);
  const [nvrChannels, setNvrChannels] = useState<{number: number, isUsed: boolean, camera: string | null}[]>([]);
  const [ipError, setIpError] = useState('');
  const [showDuplicateDetail, setShowDuplicateDetail] = useState<string | null>(null);
  const [ipWarning, setIpWarning] = useState('');

  const [formData, setFormData] = useState({
    brand_id: '',
    model_id: '',
    brand: '',
    alias: '',
    model: '',
    nvr_id: '',
    channel_number: '',
    ip_address: '',
    warranty_start_date: '',
    warranty_end_date: '',
    inventory_number: '',
    installation_location: '',
    notes: '',
    screenshot_path: ''
  });

  useEffect(() => {
    fetchCameras();
    fetchNvrs();
    fetchCameraBrands();
  }, []);

  const faultDescriptionOptions = [
    { key: 'cameras.faultDesc1', value: 'Görüntü Gelmiyor' },
    { key: 'cameras.faultDesc2', value: 'Görüntü Gelip Gidiyor' },
    { key: 'cameras.faultDesc3', value: 'Kamera Merceği Temizlenmeli' },
    { key: 'cameras.faultDesc4', value: 'Haberleşme Kablosu Arızalı' },
    { key: 'cameras.faultDesc5', value: 'Diğer' },
  ];

  const handleOpenFaultModal = (camera: Camera) => {
    setFaultCamera(camera);
    setFaultFormData({ fault_description: 'Görüntü Gelmiyor', notes: '' });
    setFaultImage(null);
    setFaultImagePreview(camera.screenshot_path ? `/uploads/${camera.screenshot_path}` : null);
    setShowFaultModal(true);
  };

  const handleFaultImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFaultImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setFaultImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleFaultSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!faultCamera) return;
    setFaultSubmitting(true);
    try {
      let imagePath = null;
      if (faultImage) {
        const imgForm = new FormData();
        imgForm.append('image', faultImage);
        const imgRes = await fetch('/api/camera-faults/upload-image', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
          body: imgForm
        });
        if (imgRes.ok) {
          const imgData = await imgRes.json();
          imagePath = imgData.filename;
        }
      }

      const savedUser = localStorage.getItem('auth_user');
      let reportedByName = '';
      if (savedUser) {
        try {
          const u = JSON.parse(savedUser);
          reportedByName = u.name || u.username || '';
        } catch {}
      }

      const payload = {
        camera_id: faultCamera.id,
        camera_name: `${faultCamera.brand} ${faultCamera.model}`,
        ip_address: faultCamera.ip_address,
        location_info: faultCamera.location_name,
        fault_description: faultFormData.fault_description,
        reported_by_name: reportedByName,
        status: 'açık',
        notes: faultFormData.notes,
        image_path: imagePath
      };

      const res = await fetch('/api/camera-faults', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowFaultModal(false);
        alert(t('cameras.alertFaultCreated'));
      } else {
        const err = await res.json();
        alert(err.error || t('cameras.alertError'));
      }
    } catch (error) {
      console.error('Arıza kaydedilemedi:', error);
      alert(t('cameras.alertError'));
    } finally {
      setFaultSubmitting(false);
    }
  };

  const fetchCameras = async () => {
    try {
      const response = await fetch('/api/cameras', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCameras(data.data || []);
      }
    } catch (error) {
      console.error('Kameralar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNvrs = async () => {
    try {
      const response = await fetch('/api/nvrs', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNvrs(data.data || []);
      }
    } catch (error) {
      console.error('NVR\'lar yüklenemedi:', error);
    }
  };

  const fetchCameraBrands = async () => {
    try {
      const response = await fetch('/api/camera-brands', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCameraBrands(data.data || []);
      }
    } catch (error) {
      console.error('Kamera markaları yüklenemedi:', error);
    }
  };

  const fetchNvrChannels = async (nvrId: number) => {
    try {
      const response = await fetch(`/api/cameras/nvr/${nvrId}/channels`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (response.ok) {
        const result = await response.json();
        setNvrChannels(result.data.channels || []);
      }
    } catch (error) {
      console.error('NVR kanalları getirilemedi:', error);
      setNvrChannels([]);
    }
  };

  const getWarrantyStatus = (endDate: string) => {
    if (!endDate) return { status: 'unknown', text: t('cameras.warrantyUnknown'), color: 'text-gray-500 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' };

    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'expired', text: t('cameras.warrantyExpired'), color: 'text-red-600', bgColor: 'bg-red-100' };
    } else if (diffDays <= 30) {
      return { status: 'expiring', text: `${diffDays}d`, color: 'text-orange-600', bgColor: 'bg-orange-100' };
    } else {
      return { status: 'active', text: t('cameras.warrantyActive'), color: 'text-green-600', bgColor: 'bg-green-100' };
    }
  };

  // Filter cameras based on selected NVR and search criteria
  const getFilteredCameras = () => {
    if (!selectedNvr) return [];

    return cameras.filter(camera => {
      if (camera.nvr_id !== selectedNvr.id) return false;

      const matchesSearch = !searchTerm || (
        String(camera.installation_location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(camera.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(camera.alias || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(camera.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(camera.ip_address || '').toLowerCase().includes(searchTerm.toLowerCase())
      );

      const matchesCameraName = !cameraNameFilter ||
        String(camera.installation_location || '').toLowerCase().includes(cameraNameFilter.toLowerCase());

      const matchesIp = !ipFilter ||
        String(camera.ip_address || '').toLowerCase().includes(ipFilter.toLowerCase());

      const matchesLocation = !locationFilter ||
        camera.location_name === locationFilter;

      const matchesBrand = !brandFilter || camera.brand === brandFilter;

      return matchesSearch && matchesCameraName && matchesIp && matchesLocation && matchesBrand;
    });
  };

  const filteredCameras = getFilteredCameras();
  const totalItems = filteredCameras.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCameras = filteredCameras.slice(startIndex, endIndex);

  const getCameraCountByNvr = (nvrId: number) => {
    return cameras.filter(c => c.nvr_id === nvrId).length;
  };

  // Lokasyonları al
  const getLocations = (): string[] => {
    const uniqueLocations = [...new Set(nvrs.map(n => n.location_name).filter(Boolean))];
    return uniqueLocations.sort();
  };

  // Seçilen lokasyonun NVR'larını al
  const getNvrsForLocation = (location: string): NVR[] => {
    return nvrs.filter(n => n.location_name === location);
  };

  // Seçilen lokasyonun NVR'ları için kamera sayısını al
  const getCameraCountByLocation = (location: string): number => {
    const nvrIds = getNvrsForLocation(location).map(n => n.id);
    return cameras.filter(c => nvrIds.includes(c.nvr_id)).length;
  };

  const validateIpAddress = (ip: string): boolean => {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipRegex.test(ip);
  };

  // Aynı IP'ye sahip kameraları bulur
  const getCamerasWithIp = (ip: string) => {
    if (!ip) return [];
    return cameras.filter(camera => camera.ip_address === ip && (!editingCamera || camera.id !== editingCamera.id));
  };

  // Duplicate IP'leri bulur (farklı NVR'larda)
  const getDuplicateIps = () => {
    const ipMap = new Map<string, Camera[]>();

    cameras.forEach(camera => {
      if (camera.ip_address) {
        if (!ipMap.has(camera.ip_address)) {
          ipMap.set(camera.ip_address, []);
        }
        ipMap.get(camera.ip_address)!.push(camera);
      }
    });

    const duplicates = new Map<string, Camera[]>();
    ipMap.forEach((cameraList, ip) => {
      if (cameraList.length > 1) {
        duplicates.set(ip, cameraList);
      }
    });

    return duplicates;
  };

  // IP adresi değiştiğinde kontrol yapıldığında
  const handleIpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const ip = e.target.value;
    setFormData({...formData, ip_address: ip});

    if (ip && !validateIpAddress(ip)) {
      setIpError(t('cameras.ipValidError'));
      setIpWarning('');
    } else {
      setIpError('');

      // Başka NVR'da aynı IP var mı kontrol et
      const existingCameras = getCamerasWithIp(ip);
      if (existingCameras.length > 0) {
        const nvrNames = [...new Set(existingCameras.map(c => c.nvr_name))].join(', ');
        setIpWarning(t('cameras.ipWarning', { nvrs: nvrNames }));
      } else {
        setIpWarning('');
      }
    }
  };

  const handleEdit = async (camera: Camera) => {
    setEditingCamera(camera);

    const formatDateForInput = (dateString: string | null | undefined): string => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch {
        return '';
      }
    };

    let brandsToUse = cameraBrands;
    if (cameraBrands.length === 0) {
      try {
        const response = await fetch('/api/camera-brands', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });
        if (response.ok) {
          const data = await response.json();
          brandsToUse = data.data || [];
        }
      } catch (error) {
        console.error('Camera brands refetch hatası:', error);
      }
    }

    const selectedBrand = brandsToUse.find(b => b.name === camera.brand);
    const selectedModel = selectedBrand?.models.find(m => m.name === camera.model);

    setFormData({
      brand_id: selectedBrand?.id.toString() || '',
      model_id: selectedModel?.id.toString() || '',
      brand: camera.brand,
      alias: camera.alias || '',
      model: camera.model,
      nvr_id: camera.nvr_id.toString(),
      channel_number: camera.channel_number?.toString() || '',
      ip_address: camera.ip_address || '',
      warranty_start_date: formatDateForInput(camera.warranty_start_date),
      warranty_end_date: formatDateForInput(camera.warranty_end_date),
      inventory_number: camera.inventory_number || '',
      installation_location: camera.installation_location || '',
      notes: camera.notes || '',
      screenshot_path: camera.screenshot_path || ''
    });

    if (camera.nvr_id) {
      fetchNvrChannels(camera.nvr_id);
    }

    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateIpAddress(formData.ip_address)) {
      alert(t('cameras.ipValidError'));
      return;
    }

    if (ipError) {
      alert(t('cameras.ipFixError'));
      return;
    }

    // Duplicate IP var mı uyarı ver ama kaydetmeye izin ver
    const existingCameras = getCamerasWithIp(formData.ip_address);
    if (existingCameras.length > 0 && !editingCamera) {
      const nvrNames = [...new Set(existingCameras.map(c => c.nvr_name))].join(', ');
      const proceed = confirm(t('cameras.ipConfirmDuplicate', { nvrs: nvrNames }));
      if (!proceed) {
        return;
      }
    }

    try {
      const url = editingCamera ? `/api/cameras/${editingCamera.id}` : '/api/cameras';
      const method = editingCamera ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        fetchCameras();
        setShowModal(false);
        resetForm();
        alert(editingCamera ? t('cameras.alertUpdated') : t('cameras.alertAdded'));
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('cameras.alertError'));
      }
    } catch (error) {
      console.error('Kamera kaydedilemedi:', error);
      alert(t('cameras.alertError'));
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm(t('cameras.alertDeleteConfirm'))) {
      try {
        const response = await fetch(`/api/cameras/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        });

        if (response.ok) {
          fetchCameras();
          alert(t('cameras.alertDeleted'));
        }
      } catch (error) {
        console.error('Kamera silinemedi:', error);
        alert(t('cameras.alertError'));
      }
    }
  };

  const handleCopy = (camera: Camera) => {
    setEditingCamera(null);

    const selectedBrand = cameraBrands.find(brand => brand.name === camera.brand);
    const selectedModel = selectedBrand?.models.find(model => model.name === camera.model);

    setFormData({
      brand_id: selectedBrand?.id.toString() || '',
      model_id: selectedModel?.id.toString() || '',
      brand: camera.brand,
      model: camera.model,
      nvr_id: camera.nvr_id.toString(),
      channel_number: '',
      ip_address: '',
      warranty_start_date: '',
      warranty_end_date: '',
      inventory_number: '',
      installation_location: camera.installation_location || '',
      notes: camera.notes || ''
    });

    if (camera.nvr_id) {
      fetchNvrChannels(camera.nvr_id);
    }

    setShowModal(true);
  };

  const generateQRCode = async (cameraId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
      const url = `${siteUrl}/api/cameras/${cameraId}/qr-code?token=${token}`;
      window.open(url, '_blank');
    } catch (error) {
      console.error('QR kod oluşturma hatası:', error);
    }
  };

  const exportToExcel = () => {
    const locale = language === 'en' ? 'en-US' : 'tr-TR';
    const data = filteredCameras.map(camera => ({
      [t('cameras.excelBrand')]: camera.brand,
      [t('cameras.excelModel')]: camera.model,
      [t('cameras.excelAlias')]: camera.alias || '',
      [t('cameras.excelInventory')]: camera.inventory_number,
      [t('cameras.excelNvr')]: camera.nvr_name,
      [t('cameras.excelChannel')]: camera.channel_number,
      [t('cameras.excelIp')]: camera.ip_address,
      [t('cameras.excelInstallLocation')]: camera.installation_location,
      [t('cameras.excelWarrantyStart')]: camera.warranty_start_date ? new Date(camera.warranty_start_date).toLocaleDateString(locale) : '',
      [t('cameras.excelWarrantyEnd')]: camera.warranty_end_date ? new Date(camera.warranty_end_date).toLocaleDateString(locale) : '',
      [t('cameras.excelNotes')]: camera.notes,
      [t('cameras.excelCreatedAt')]: new Date(camera.created_at).toLocaleDateString(locale)
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cameras');
    XLSX.writeFile(wb, `cameras_${new Date().toLocaleDateString(locale).replace(/\./g, '_')}.xlsx`);
  };

  const resetForm = () => {
    setFormData({
      brand_id: '', model_id: '', brand: '', alias: '', model: '', nvr_id: '', channel_number: '', ip_address: '',
      warranty_start_date: '', warranty_end_date: '', inventory_number: '',
      installation_location: '', notes: '', screenshot_path: ''
    });
    setEditingCamera(null);
    setIpError('');
    setIpWarning('');
  };

  const handleAddBrand = async () => {
    if (!newBrandName.trim()) {
      alert(t('cameras.alertBrandRequired'));
      return;
    }

    try {
      const response = await fetch('/api/camera-brands', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ name: newBrandName.trim() })
      });

      if (response.ok) {
        fetchCameraBrands();
        setNewBrandName('');
        setShowBrandModal(false);
        alert(t('cameras.alertBrandAdded'));
      } else {
        const errorData = await response.json();
        alert(errorData.error || t('cameras.alertError'));
      }
    } catch (error) {
      console.error('Marka eklenemedi:', error);
      alert(t('cameras.alertError'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{t('cameras.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">{t('cameras.totalCount', { count: cameras.length })}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-purple-600 text-white' : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            {t('cameras.filter')}
          </button>
          <button
            onClick={exportToExcel}
            disabled={!selectedNvr}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('cameras.newCamera')}
          </button>
        </div>
      </div>

      {/* Lokasyon Kartları */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('cameras.locations')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {getLocations().map((location) => {
            const cameraCount = getCameraCountByLocation(location);
            const nvrCount = getNvrsForLocation(location).length;
            const isSelected = selectedLocation === location;

            return (
              <button
                key={location}
                onClick={() => {
                  setSelectedLocation(isSelected ? null : location);
                  setSelectedNvr(null);
                  setCurrentPage(1);
                  setSearchTerm('');
                }}
                className={`p-5 rounded-lg border-2 transition-all text-left shadow-sm hover:shadow-md ${
                  isSelected
                    ? 'border-primary bg-primary/10 dark:bg-primary/90'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary/50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg flex items-center">
                      <MapPin className="w-5 h-5 mr-2 text-primary" />
                      {location}
                    </h3>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded p-2 text-center">
                    <div className="text-xl font-bold text-primary dark:text-primary">{nvrCount}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">NVR</div>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-700 rounded p-2 text-center">
                    <div className="text-xl font-bold text-green-600 dark:text-green-400">{cameraCount}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{t('cameras.title')}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* NVR Kartları - Seçili Lokasyon İçin */}
      {selectedLocation && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('cameras.nvrTitle', { location: selectedLocation })}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {getNvrsForLocation(selectedLocation).map((nvr) => {
              const cameraCount = getCameraCountByNvr(nvr.id);
              const isSelected = selectedNvr?.id === nvr.id;

              return (
                <button
                  key={nvr.id}
                  onClick={() => {
                    setSelectedNvr(isSelected ? null : nvr);
                    setCurrentPage(1);
                    setSearchTerm('');
                  }}
                  className={`p-4 rounded-lg border-2 transition-all text-left shadow-sm hover:shadow-md ${
                    isSelected
                      ? 'border-primary bg-primary/10 dark:bg-primary/90'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                        <Monitor className="w-4 h-4 mr-2 text-purple-500" />
                        {nvr.name}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-mono text-xs">{nvr.ip_address}</p>
                    </div>
                    <div className={`text-2xl font-bold ${isSelected ? 'text-primary' : 'text-gray-400'}`}>
                      {cameraCount}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Seçili NVR'ın Kameraları */}
      {selectedNvr && (
        <>
          {/* Arama ve Filtreler */}
          <div className="mb-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('cameras.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>

            {/* Filtreler */}
            {showFilters && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">{t('cameras.filterTitle')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('cameras.filterCameraName')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('cameras.placeholderCameraName')}
                      value={cameraNameFilter}
                      onChange={(e) => {
                        setCameraNameFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('cameras.filterIp')}
                    </label>
                    <input
                      type="text"
                      placeholder={t('cameras.placeholderIp')}
                      value={ipFilter}
                      onChange={(e) => {
                        setIpFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('cameras.filterBrand')}
                    </label>
                    <select
                      value={brandFilter}
                      onChange={(e) => {
                        setBrandFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">{t('cameras.allBrands')}</option>
                      {[...new Set(
                        cameras
                          .filter(c => c.nvr_id === selectedNvr?.id)
                          .map(c => c.brand)
                      )].map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {(cameraNameFilter || ipFilter || brandFilter) && (
                  <button
                    onClick={() => {
                      setCameraNameFilter('');
                      setIpFilter('');
                      setBrandFilter('');
                      setCurrentPage(1);
                    }}
                    className="mt-3 text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                  >
                    {t('cameras.clearFilters')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Kamera Tablosu */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-auto divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('cameras.colCamera')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('cameras.colChannel')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('cameras.colLocation')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('cameras.colWarranty')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase tracking-wider">
                      {t('cameras.colActions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedCameras.length > 0 ? (
                    paginatedCameras.map((camera) => {
                      const warranty = getWarrantyStatus(camera.warranty_end_date);
                      return (
                        <tr key={camera.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors">
                          <td className="px-6 py-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center">
                                <Camera className="w-4 h-4 mr-2 text-primary" />
                                {camera.brand} {camera.model}
                                {camera.screenshot_path && (
                                  <button
                                    onClick={() => setPreviewScreenshot({camera, path: camera.screenshot_path!})}
                                    className="ml-2 text-green-500 hover:text-green-700 hover:bg-green-100 dark:hover:bg-green-900 p-1 rounded transition-colors"
                                    title="Görüntüyü göster"
                                  >
                                    <ImageIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                              {camera.alias && (
                                <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-1">
                                  {camera.alias}
                                </div>
                              )}
                              {camera.inventory_number && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                                  <Package className="w-3 h-3 mr-1" />
                                  {camera.inventory_number}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="text-gray-900 dark:text-gray-100 font-medium">
                                {t('cameras.channel', { number: camera.channel_number })}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {maskIpAddress(camera.ip_address)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <div className="text-gray-900 dark:text-gray-100 font-medium flex items-center">
                                <MapPin className="w-3 h-3 mr-1" />
                                {camera.installation_location || t('cameras.unspecified')}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {camera.location_name}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm">
                              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${warranty.bgColor} ${warranty.color}`}>
                                {warranty.text}
                              </span>
                              {camera.warranty_end_date && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                                  <Calendar className="w-3 h-3 mr-1" />
                                  {new Date(camera.warranty_end_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(camera)}
                                className="text-primary hover:text-primary-dark dark:text-primary hover:bg-primary/20 dark:hover:bg-primary/90 p-2 rounded"
                                title={t('cameras.titleEdit')}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleCopy(camera)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900 p-2 rounded"
                                title={t('cameras.titleCopy')}
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => generateQRCode(camera.id)}
                                className="text-purple-600 hover:text-purple-900 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900 p-2 rounded"
                                title={t('cameras.titleQr')}
                              >
                                <QrCode className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleOpenFaultModal(camera)}
                                className="text-orange-600 hover:text-orange-900 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900 p-2 rounded"
                                title={t('cameras.titleFault')}
                              >
                                <AlertTriangle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(camera.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 p-2 rounded"
                                title={t('cameras.titleDelete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                        {t('cameras.noCamera')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white dark:bg-gray-800 px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {totalItems > 0 ? (
                    t('cameras.paginationInfo', { start: startIndex + 1, end: Math.min(endIndex, totalItems), total: totalItems })
                  ) : (
                    t('cameras.noRecords')
                  )}
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {t('cameras.prevPage')}
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 border rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-primary text-white border-primary'
                            : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {t('cameras.nextPage')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Duplicate Cameras Section */}
          {(() => {
            const duplicateIps = getDuplicateIps();
            return duplicateIps.size > 0 ? (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <span className="w-8 h-8 bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mr-3 text-lg">!</span>
                  {t('cameras.duplicateIps', { count: duplicateIps.size })}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Array.from(duplicateIps.entries()).map(([ip, camerasWithIp]) => (
                    <div
                      key={ip}
                      onClick={() => setShowDuplicateDetail(showDuplicateDetail === ip ? null : ip)}
                      className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-shadow"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-2">
                            IP: {ip}
                          </h3>
                          <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                            {t('cameras.duplicateFoundIn', { count: camerasWithIp.length })}
                          </p>
                        </div>
                        <ChevronDown
                          className={`w-5 h-5 text-red-600 dark:text-red-400 transition-transform ${
                            showDuplicateDetail === ip ? 'transform rotate-180' : ''
                          }`}
                        />
                      </div>

                      {showDuplicateDetail === ip && (
                        <div className="mt-4 pt-4 border-t-2 border-red-200 dark:border-red-800">
                          <div className="space-y-3">
                            {camerasWithIp.map((camera) => (
                              <div
                                key={camera.id}
                                className="bg-white dark:bg-gray-800 rounded p-3 flex items-center justify-between"
                              >
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {camera.nvr_name}
                                  </div>
                                  <div className="text-sm text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">{t('cameras.channel', { number: camera.channel_number })}</span> • {camera.brand} {camera.model}
                                  </div>
                                  {camera.alias && (
                                    <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-1">
                                      {camera.alias}
                                    </div>
                                  )}
                                  {camera.installation_location && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                                      <MapPin className="w-3 h-3 mr-1" />
                                      {camera.installation_location}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(camera);
                                  }}
                                  className="ml-2 px-2 py-1 bg-primary text-white rounded text-xs hover:bg-primary-dark"
                                >
                                  {t('cameras.titleEdit')}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
        </>
      )}

      {!selectedLocation && !selectedNvr && (
        <div className="bg-primary/10 dark:bg-primary/90 border border-primary/30 dark:border-primary-dark rounded-lg p-8 text-center">
          <MapPin className="w-16 h-16 mx-auto text-primary mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('cameras.selectLocationPrompt')}</h3>
          <p className="text-gray-700 dark:text-gray-300">
            {t('cameras.selectLocationDesc')}
          </p>
        </div>
      )}

      {selectedLocation && !selectedNvr && (
        <div className="bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-800 rounded-lg p-8 text-center">
          <Monitor className="w-16 h-16 mx-auto text-green-500 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('cameras.selectNvrPrompt')}</h3>
          <p className="text-gray-700 dark:text-gray-300">
            {t('cameras.selectNvrDesc', { location: selectedLocation })}
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {editingCamera ? t('cameras.modalEditTitle') : (formData.brand && formData.model ? t('cameras.modalCopyTitle') : t('cameras.modalNewTitle'))}
              </h2>
              <button onClick={() => {
                setShowModal(false);
                resetForm();
              }}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelBrand')}</label>
                  <div className="flex gap-2">
                    <select
                      required
                      value={formData.brand_id}
                      onChange={(e) => {
                        const selectedBrand = cameraBrands.find(b => b.id.toString() === e.target.value);
                        setFormData({
                          ...formData,
                          brand_id: e.target.value,
                          brand: selectedBrand?.name || '',
                          model_id: '',
                          model: ''
                        });
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">{t('cameras.placeholderBrand')}</option>
                      {cameraBrands.map(brand => (
                        <option key={brand.id} value={brand.id.toString()}>{brand.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setShowBrandModal(true)}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                      title={t('cameras.newBrandTooltip')}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelModel')}</label>
                  <select
                    required
                    value={formData.model_id}
                    onChange={(e) => {
                      const selectedBrand = cameraBrands.find(b => b.id.toString() === formData.brand_id);
                      const selectedModel = selectedBrand?.models.find(m => m.id.toString() === e.target.value);
                      setFormData({
                        ...formData,
                        model_id: e.target.value,
                        model: selectedModel?.name || ''
                      });
                    }}
                    disabled={!formData.brand_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                  >
                    <option value="">{t('cameras.placeholderModel')}</option>
                    {cameraBrands
                      .find(b => b.id.toString() === formData.brand_id)
                      ?.models.map(model => (
                        <option key={model.id} value={model.id.toString()}>{model.name}</option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelAlias')}</label>
                  <input
                    type="text"
                    value={formData.alias}
                    onChange={(e) => setFormData({...formData, alias: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    placeholder={t('cameras.placeholderAlias')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelNvr')}</label>
                  <select
                    required
                    value={formData.nvr_id}
                    onChange={(e) => {
                      const nvrId = e.target.value;
                      setFormData({...formData, nvr_id: nvrId, channel_number: ''});
                      if (nvrId) {
                        fetchNvrChannels(parseInt(nvrId));
                      } else {
                        setNvrChannels([]);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">{t('cameras.placeholderNvr')}</option>
                    {nvrs.map(nvr => (
                      <option key={nvr.id} value={nvr.id.toString()}>{nvr.name} ({nvr.location_name})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelChannelNumber')}</label>
                  <select
                    required
                    value={formData.channel_number}
                    onChange={(e) => setFormData({...formData, channel_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                    disabled={!formData.nvr_id}
                  >
                    <option value="">
                      {!formData.nvr_id ? t('cameras.placeholderChannelNvr') : t('cameras.placeholderChannel')}
                    </option>
                    {nvrChannels.map(channel => (
                      <option
                        key={channel.number}
                        value={channel.number.toString()}
                        disabled={channel.isUsed}
                        style={{
                          color: channel.isUsed ? '#ef4444' : 'inherit',
                          backgroundColor: channel.isUsed ? '#fef2f2' : 'inherit'
                        }}
                      >
                        {t('cameras.channel', { number: channel.number })} {channel.isUsed ? t('cameras.channelFull', { camera: channel.camera }) : t('cameras.channelEmpty')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelIp')}</label>
                  <input
                    type="text"
                    required
                    value={formData.ip_address}
                    onChange={handleIpChange}
                    placeholder="192.168.1.100"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white transition-colors ${
                      ipError
                        ? 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                        : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {ipError && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1 flex items-center">
                      <span className="inline-block w-1 h-1 bg-red-600 dark:bg-red-400 rounded-full mr-2"></span>
                      {ipError}
                    </p>
                  )}
                  {ipWarning && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-1 flex items-center">
                      <span className="inline-block w-1 h-1 bg-orange-600 dark:bg-orange-400 rounded-full mr-2"></span>
                      {ipWarning}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelWarrantyStart')}</label>
                  <input
                    type="date"
                    value={formData.warranty_start_date}
                    onChange={(e) => setFormData({...formData, warranty_start_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelWarrantyEnd')}</label>
                  <input
                    type="date"
                    value={formData.warranty_end_date}
                    onChange={(e) => setFormData({...formData, warranty_end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelInventory')}</label>
                  <input
                    type="text"
                    value={formData.inventory_number}
                    onChange={(e) => setFormData({...formData, inventory_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelInstallLocation')}</label>
                  <input
                    type="text"
                    value={formData.installation_location}
                    onChange={(e) => setFormData({...formData, installation_location: e.target.value})}
                    placeholder={t('cameras.placeholderInstallLocation')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Screenshot Capture */}
              <div className="md:col-span-2">
                <CameraScreenshotCapture
                  currentScreenshot={formData.screenshot_path}
                  onScreenshotCapture={(path) => setFormData({...formData, screenshot_path: path})}
                  onScreenshotRemove={() => setFormData({...formData, screenshot_path: ''})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('cameras.labelNotes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('cameras.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={!!ipError || !formData.ip_address}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    ipError || !formData.ip_address
                      ? 'bg-gray-400 text-gray-600 dark:text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary-dark'
                  }`}
                  title={ipError ? t('cameras.ipFixError') : !formData.ip_address ? t('cameras.labelIp') : ''}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {t('cameras.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Yeni Marka Ekleme Modal */}
      {showBrandModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('cameras.addBrandModalTitle')}
              </h2>
              <button onClick={() => setShowBrandModal(false)}>
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('cameras.labelBrandName')}
                </label>
                <input
                  type="text"
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  placeholder="örn: Hikvision"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddBrand()}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowBrandModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700"
                >
                  {t('cameras.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleAddBrand}
                  className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('cameras.addBrandBtn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Preview Modal */}
      {previewScreenshot && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4" onClick={() => setPreviewScreenshot(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-primary" />
                  {previewScreenshot.camera.brand} {previewScreenshot.camera.model}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {previewScreenshot.camera.alias && `${previewScreenshot.camera.alias} • `}
                  {t('cameras.channel', { number: previewScreenshot.camera.channel_number })} • {previewScreenshot.camera.location_name}
                </p>
              </div>
              <button
                onClick={() => setPreviewScreenshot(null)}
                className="p-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-100 dark:bg-gray-700 dark:bg-gray-900" style={{minHeight: '400px'}}>
              <img
                src={`/uploads/${previewScreenshot.path}`}
                alt={`${previewScreenshot.camera.brand} ${previewScreenshot.camera.model} görüntüsü`}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-camera.png';
                }}
              />
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                IP: {previewScreenshot.camera.ip_address}
              </div>
              <button
                onClick={() => setPreviewScreenshot(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                {t('cameras.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Arıza Kaydı Modal */}
      <Modal isOpen={showFaultModal} onClose={() => setShowFaultModal(false)}>
        <div
          className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">{t('cameras.faultModalTitle')}</h2>
            </div>
            <button onClick={() => setShowFaultModal(false)}>
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>

          {faultCamera && (
            <div className="mb-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {faultCamera.brand} {faultCamera.model}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {faultCamera.location_name} • {t('cameras.channel', { number: faultCamera.channel_number })} • {faultCamera.nvr_name}
              </p>
            </div>
          )}

          <form onSubmit={handleFaultSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('cameras.faultType')}
              </label>
              <select
                required
                value={faultFormData.fault_description}
                onChange={(e) => setFaultFormData({ ...faultFormData, fault_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                {faultDescriptionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{t(opt.key as any)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('cameras.faultNotes')}
              </label>
              <textarea
                value={faultFormData.notes}
                onChange={(e) => setFaultFormData({ ...faultFormData, notes: e.target.value })}
                rows={3}
                placeholder={t('cameras.placeholderFaultDetails')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('cameras.faultLastImage')}
              </label>
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                {faultImagePreview ? (
                  <div className="space-y-2">
                    <img
                      src={faultImagePreview}
                      alt="Önizleme"
                      className="w-full max-h-48 object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={() => { setFaultImagePreview(null); setFaultImage(null); }}
                      className="w-full px-3 py-2 text-sm text-red-600 hover:text-red-900 border border-red-300 rounded-lg"
                    >
                      {t('cameras.faultImageRemove')}
                    </button>
                  </div>
                ) : (
                  <label className="cursor-pointer">
                    <div className="flex flex-col items-center justify-center py-6">
                      <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">{t('cameras.faultImageUpload')}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('cameras.faultImageHint')}</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleFaultImageChange} className="hidden" />
                  </label>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setShowFaultModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                {t('cameras.cancel')}
              </button>
              <button
                type="submit"
                disabled={faultSubmitting}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {faultSubmitting ? t('cameras.faultSubmitting') : t('cameras.faultSubmit')}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default CamerasPage;
